/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react'
import { Flex } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
// import { useNavigate } from '@tanstack/react-router'
import { IconSearch } from '@tabler/icons-react'
import { classService } from '@/services/classService'
import { practiceTestService } from '@/services/practiceTestService'
import { useParticipantStore } from '@/stores/participantStore'
import { cn, truncate } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import {
  FormatFullDateMonthYearHourMinute,
  FormatFullDateMonthYearHourMinuteSecond,
} from '@/utils/time'
import { useHeroNavbar } from '@/context/hero-navbar-context'
import { useDebounce } from '@/hooks/useDebounce'
import { useGetPracTestListParticipants } from '@/hooks/usePracticeTest'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import Hstack from '@/components/ui/hstack'
import { Input } from '@/components/ui/input'
import Pagination from '@/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import BreadScrum from '@/components/BreadScrum'
import PageTitle from '@/components/PageTitle'
// import { ClassDetailTabs } from '@/features/classes/constants'
import { Main } from '@/components/layout/main'
import MockTestResult from './components/result-drawer'

export const ParticipantList = () => {
  //   const navigate = useNavigate()
  const { ptId, classId: classIdParams } = useParams({
    from: '/_authenticated/marking/list-participant/$ptId/$classId',
  })
  const sourceParams = useSearch({
    from: '/_authenticated/marking/list-participant/$ptId/$classId',
  }) as { source?: string }
  const isFromClassDetailMock = sourceParams?.source === 'classDetailMockExam'
  const isFromExamTable = sourceParams?.source === 'ExamTable'

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const debouncedQuery = useDebounce(search) // wait 500ms
  const { setHeading, setVariant, setBreadcrumbs, setHeight } = useHeroNavbar()
  const {
    data: participantData,
    setParticipantData,
    hasHydrated,
  } = useParticipantStore()
  // const [result, setResult] = useState<Result | null>(null)
  // const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isResultOpen, setIsResultOpen] = useState(false)
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  )
  const [limit] = useState(10)

  const searchParams = new URLSearchParams(window.location.search)
  const classIdFromSearch = searchParams.get('classId')
  const moduleIdFromSearch = searchParams.get('moduleId')
  const ptIdFromSearch =
    searchParams.get('ptId') || searchParams.get('practiceTestId') || ptId

  const classId = participantData?.classId || classIdFromSearch
  const moduleId = participantData?.moduleId || moduleIdFromSearch
  const practiceTestId = ptIdFromSearch || participantData?.examTestData?.id

  // Derive IDs for hooks (must be before any conditional returns)
  const practiceTestIdNumber = Number(practiceTestId)

  const { data: practiceTestDetailData, isLoading: isLoadingTestDetails } =
    useQuery({
      queryKey: ['practiceTestDetail', ptId, page],
      queryFn: () =>
        practiceTestService.getPracticeTest({
          practice_test_id: ptId,
          size: 10,
          page,
        }),
    })

  // Fetch participants list (keep hook order stable)
  const participantsParamsBase = {
    practice_test_id: Number(ptId),
    class_id: classIdParams,
    search: debouncedQuery || null,
    page: page,
    limit: limit,
  }
  // const participantsParams =
  //   result === null
  //     ? participantsParamsBase
  //     : { ...participantsParamsBase, result }

  const { data: participantsRes, isLoading: isLoadingParticipants } =
    useGetPracTestListParticipants(participantsParamsBase as any)

  const { data: classDetail } = useQuery({
    queryKey: ['class', classId],
    queryFn: () => classService.getClassById(Number(classId)),
    enabled: !!classId && !participantData,
  })

  useEffect(() => {
    setVariant('pattern')
    setHeight(230)
  }, [setVariant, setHeight])

  useEffect(() => {
    if (!hasHydrated) return
    if (!participantData) {
      setHeading(
        <div>
          <Hstack className='font-mundial mb-1 flex items-center text-5xl font-[600]'>
            Participant List
          </Hstack>
          <Hstack gapX={'2'}>
            <span className='font-light'>Loading participant data...</span>
          </Hstack>
        </div>
      )
      setBreadcrumbs([
        { label: 'Class', href: '/classes' },
        { label: 'Participant List', href: '' },
      ])
    }
  }, [hasHydrated, participantData, setHeading, setBreadcrumbs])

  // const getTabKey = (tabName: string): string => {
  //   const tabMap: Record<string, string> = {
  //     [ClassDetailTabs.chapter]: 'chapter',
  //     [ClassDetailTabs.student]: 'student',
  //     [ClassDetailTabs.mock_exam]: 'mock_exam',
  //     [ClassDetailTabs.practice_test]: 'practice_test',
  //   }
  //   return tabMap[tabName] || 'chapter'
  // }

  useEffect(() => {
    if (!hasHydrated) return
    if (classDetail && !participantData && classId && moduleId) {
      setParticipantData({
        activeTab: 'practice_test', //ClassDetailTabs?.practice_test
        classId: classId,
        moduleId: moduleId,
        examTestData: null,
        className: classDetail.class_name || '',
        moduleName: classDetail.module_name || '',
        tutors: classDetail.tutors_name || [],
      })
    }
  }, [
    hasHydrated,
    classDetail,
    participantData,
    classId,
    moduleId,
    setParticipantData,
  ])

  useEffect(() => {
    if (!hasHydrated) return
    if (!participantData && !classId && !moduleId) {
      setParticipantData({
        activeTab: 'practice_test', //ClassDetailTabs.practice_test
        classId: 'unknown',
        moduleId: 'unknown',
        examTestData: null,
        className: 'Unknown Class',
        moduleName: 'Unknown Module',
        tutors: [],
      })
    }
  }, [hasHydrated, participantData, classId, moduleId, setParticipantData])

  useEffect(() => {
    if (participantData) {
      const className = participantData.className || '-'
      const headerItem = participantsRes?.data?.[0]
      const examTestName = headerItem?.practice_test_name || 'Exam name'
      const createdAt = headerItem?.created_at || null
      const createdBy =
        headerItem?.created_by === 'admin'
          ? 'Admin'
          : headerItem?.created_by || null

      setHeading(
        <div>
          <Hstack className='font-mundial mb-1 flex items-center text-5xl font-[600]'>
            {examTestName}
          </Hstack>
          <Hstack gapX={'2'}>
            <span className='font-light'>Created date:</span>{' '}
            {createdAt ? FormatFullDateMonthYearHourMinute(createdAt) : '-'}
            <span className='font-light'>Created by:</span> {createdBy || '-'}
          </Hstack>
        </div>
      )
      setBreadcrumbs([
        { label: 'Class', href: '/class' },
        {
          label: truncate(className, 30),
          // href: `/classes/detail/${participantData.classId}/${participantData.moduleId}`,
        },
        {
          label: participantData.activeTab,
          // href: `/classes/detail/${participantData.classId}/${participantData.moduleId}?tab=${getTabKey(participantData.activeTab)}`,
        },
        { label: examTestName, href: `` },
      ])
    }
  }, [participantData, participantsRes, setHeading, setBreadcrumbs])

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  if (!hasHydrated) {
    return <div />
  }

  if (!participantData && !classDetail) {
    return <div>Loading...</div>
  }

  if (!participantData) {
    return null
  }

  return (
    <>
      <Main className='w-full'>
        {!isLoadingTestDetails && (
          <BreadScrum
            breadcrumbs={[
              {
                link: isFromClassDetailMock ? '/class/' : '/test/',
                label: isFromClassDetailMock
                  ? 'Class management'
                  : 'Test management',
              },
              {
                link: '',
                label: practiceTestDetailData?.module_name ?? '',
              },
              {
                link: '',
                label:
                  isFromClassDetailMock || isFromExamTable
                    ? 'Mock exam'
                    : 'Practice test',
              },
              {
                link: '',
                label: practiceTestDetailData?.pt_name ?? '',
              },
            ]}
          />
        )}
        <div className='mb-4'>
          <div className='flex items-center gap-4'>
            <PageTitle title={practiceTestDetailData?.pt_name ?? ''} />
          </div>
          <div className='flex items-center gap-8'>
            <p>
              <span className='font-light'>Created date:</span>{' '}
              {FormatFullDateMonthYearHourMinute(
                practiceTestDetailData?.created_at || '-'
              )}
            </p>
            <p>
              <span className='font-light'>Created by:</span>{' '}
              {practiceTestDetailData?.owner || '-'}
            </p>
          </div>
        </div>
        <Card>
          <CardContent className='p-0'>
            <div className={'space-y-4 rounded-lg bg-[#FFFFFF] p-4'}>
              <div className='flex items-center justify-between'>
                <h1 className='text-2xl font-[400] text-[#002652]'>Marking</h1>
              </div>
              <Flex align={'center'} justify={'between'} className='max-h-10'>
                <div className='relative'>
                  <IconSearch className='text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2' />
                  <Input
                    placeholder={'Search'}
                    value={search}
                    onChange={(event) => setSearch(event.target.value.trim())}
                    className='h-10 w-[150px] pl-9 lg:w-[250px]'
                  />
                </div>

                {/* <Box>
                  <DropdownMenu onOpenChange={setIsDropdownOpen}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='outline'
                        className='inline-flex h-8 w-[180px] items-center justify-between gap-1 px-3 lg:px-4'
                      >
                        <span className='truncate'>
                          Result: {result ? result.replace(/_/g, ' ') : 'All'}
                        </span>
                        {isDropdownOpen ? (
                          <IconChevronUp className='size-4 flex-shrink-0' />
                        ) : (
                          <IconChevronDown className='size-4 flex-shrink-0' />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem
                        key='all'
                        className={cn(
                          'cursor-pointer px-3 py-2 text-sm',
                          result === null
                            ? 'bg-gray-100 text-[#002652]'
                            : 'text-[#475467] hover:bg-gray-100'
                        )}
                        onSelect={() => setResult(null)}
                      >
                        All
                      </DropdownMenuItem>
                      {Object.values(Result).map((item: string) => (
                        <DropdownMenuItem
                          key={item}
                          className={cn(
                            'cursor-pointer px-3 py-2 text-sm',
                            result === (item as Result)
                              ? 'bg-gray-100 text-[#002652]'
                              : 'text-[#475467] hover:bg-gray-100'
                          )}
                          onSelect={() => setResult(item as Result)}
                        >
                          {item.replace(/_/g, ' ')}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </Box> */}
              </Flex>
            </div>
            <ParticipantTable
              data={participantsRes?.data || []}
              isLoading={isLoadingParticipants}
              moduleId={moduleId || ''}
              classId={classId || ''}
              onViewDetail={(studentId: number) => {
                setSelectedStudentId(studentId)
                setIsResultOpen(true)
              }}
            />

            {participantsRes && participantsRes?.meta?.total_pages > 1 && (
              <div className='mt-[15px] px-[24px] py-[16px]'>
                <Pagination
                  justify={'end'}
                  currentPage={participantsRes?.meta?.current_page || page}
                  onPageChange={handlePageChange}
                  totalPages={participantsRes?.meta?.total_pages || 1}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </Main>
      {isResultOpen && selectedStudentId != null && (
        <MockTestResult
          isOpen={true}
          onClose={() => setIsResultOpen(false)}
          practiceTestId={String(practiceTestIdNumber || '')}
          studentId={selectedStudentId}
        />
      )}
    </>
  )
}

function ParticipantTable({
  data,
  isLoading,
  //   moduleId,
  //   classId,
  onViewDetail,
}: {
  data?: any[]
  isLoading?: boolean
  moduleId: string
  classId: string
  onViewDetail: (studentId: number) => void
}) {
  return (
    <Table className='w-full table-fixed'>
      <colgroup>
        <col className='w-3/7' />
        <col className='w-1/7' />
        <col className='w-2/7' />
        <col className='w-1/7' />
        <col className='w-1/7' />
        <col className='w-1/7' />
        {/* <col className='w-1/7' /> */}
        <col className='w-1/7' />
      </colgroup>
      <TableHeader>
        <TableRow className='bg-[#F2F4F7]'>
          <TableHead className='font-mundial px-[24px] py-[12px] text-sm font-[600] text-[#002652]'>
            Name
          </TableHead>
          <TableHead className='font-mundial text-left text-sm font-[600] text-[#002652]'>
            Attempts
          </TableHead>
          <TableHead className='font-mundial w-fit text-left text-sm font-[600] text-[#002652]'>
            Time
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Unmarked
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Average AI mark
          </TableHead>
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]'>
            Average Tutor Mark
          </TableHead>
          {/* <TableHead className='font-mundial text-left text-sm font-[600] text-[#002652]'>
            Class
          </TableHead> */}
          <TableHead className='font-mundial text-center text-sm font-[600] text-[#002652]' />
        </TableRow>
      </TableHeader>
      <TableBody className='[&_tr:last-child]:border-b-1'>
        {(!data || data.length === 0) && !isLoading && (
          <TableRow>
            <TableCell colSpan={8} className='h-24 text-center'>
              No data found.
            </TableCell>
          </TableRow>
        )}
        {data?.map((item: any, idx: number) => (
          <TableRow
            key={`${item.student_id}-${idx}`}
            className='hover:bg-gray-50'
          >
            <TableCell className='px-[24px] py-[12px]'>
              {item.student_name || 'N/A'}
            </TableCell>
            <TableCell className='px-[24px] py-[12px]'>
              {item.total_attempts ?? 'N/A'}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-start text-sm font-[300] text-[#002652]'
              )}
            >
              {item.last_activity
                ? FormatFullDateMonthYearHourMinuteSecond(item.last_activity)
                : 'N/A'}
            </TableCell>
            <TableCell className='text-center text-sm font-[300] text-[#002652]'>
              {item.total_unmarked
                ? `${item.total_unmarked}/${item.total_attempts}`
                : 'N/A'}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-center text-sm font-[300] text-[#002652]',
                getMarksStyle(item.ai_marks)
              )}
            >
              {getMarksPercentage(item.ai_marks)}
            </TableCell>
            <TableCell
              className={cn(
                'truncate text-center text-sm font-[300] text-[#002652]',
                getMarksStyle(item.tutor_marks)
              )}
            >
              {getMarksPercentage(item.tutor_marks)}
            </TableCell>
            {/* <TableCell className='truncate text-left text-sm font-[300] text-[#002652]'>
              {item.class_name ?? 'N/A'}
            </TableCell> */}
            <TableCell className='w-[3%] pr-5 text-center'>
              <Button
                variant='ghost'
                className='px-6 py-2 text-sm font-medium text-[#0057B8] hover:bg-[#0057B8]/10 hover:text-[#0057B8]'
                onClick={() => onViewDetail(item.student_id)}
              >
                View detail
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
